import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '../constants';
import API_ROUTES from '../constants/apiRoutes';
import { getData, patchData, postData } from '../utils/api';
import { usePagination } from '../hooks/usePagination';
import { cryptoHealthSchema, type CryptoCurrencySettings } from '../schema/cryptoDashboard';

export type { CryptoHealth, CryptoStateAge, CryptoQueueHealth } from '../schema/cryptoDashboard';

export type CryptoOverview = {
  marketplaceId: string;
  merchantId: string;
  staticWallets: { new: number; unknown: number; ready: number };
  deposits: { awaiting_verification: number; verification_enqueued: number; ready: number; review: number };
};
export type CryptoCurrency = {
  currency: string;
  providerActive: boolean;
  policyVersion?: string;
  catalogStale?: boolean;
  providerFetchedAt?: string | null;
  providerExpiresAt?: string | null;
  symbol?: string;
  network?: string | null;
  tagRequired?: boolean;
  platformDepositSupported?: boolean;
  platformWithdrawalSupported?: boolean;
  effectiveDepositEnabled?: boolean;
  effectiveWithdrawalEnabled?: boolean;
  depositReasons?: string[];
  withdrawalReasons?: string[];
  depositEnabled: boolean;
  withdrawalEnabled: boolean;
  minimumDeposit: string | number | null;
  networkFee: string | number | null;
  maximumDeposit?: string | null;
  minimumWithdrawal?: string | null;
  processingFee?: string | null;
  waitingTimeMinutes?: string | null;
  contractAddress?: string | null;
};
export type CryptoStaticWallet = {
  id: string;
  clientId: string;
  playerId: string;
  username?: string | null;
  email?: string | null;
  merchantId: string;
  currency: string;
  state: string;
  address: string | null;
  tag: string | null;
  createdAt: string;
  updatedAt: string;
};
export type CryptoAudit = {
  id: string;
  actorId: string;
  action: string;
  reason: string;
  createdAt: string;
  before: unknown;
  after: unknown;
};
export type CryptoSectionRows = {
  currencies: CryptoCurrency;
  'static-wallets': CryptoStaticWallet;
  audit: CryptoAudit;
};

export type CryptoSection = keyof CryptoSectionRows;
export const cryptoDashboardPath = (marketplaceId: string, section: string) =>
  `${API_ROUTES.CRYPTO_DASHBOARD}/${encodeURIComponent(marketplaceId)}/${section}`;

export const useCryptoOverview = (marketplaceId: string) =>
  useQuery({
    queryKey: [QUERY_KEYS.cryptoDashboard, marketplaceId, 'overview'],
    queryFn: () => getData<CryptoOverview>(cryptoDashboardPath(marketplaceId, 'overview'), undefined, 'crm'),
    enabled: !!marketplaceId,
  });

export const useCryptoList = <Section extends CryptoSection>(
  marketplaceId: string,
  section: Section,
  search?: string
) =>
  usePagination<CryptoSectionRows[Section]>(
    cryptoDashboardPath(marketplaceId, section),
    search ? { page: 1, limit: 25, search } : undefined,
    { enabled: !!marketplaceId },
    'crm'
  );

export const useRefreshCryptoCurrencies = (marketplaceId: string) => {
  const cache = useQueryClient();
  const path = cryptoDashboardPath(marketplaceId, 'currencies');
  return useMutation({
    retry: false,
    mutationFn: () => {
      if (!marketplaceId) throw new Error('Select a marketplace first');
      // Refresh the provider snapshot once, then reload active pages with their existing filters.
      return getData(path, { page: 1, limit: 1, refresh: 'true' }, 'crm');
    },
    onSuccess: () => cache.invalidateQueries({ queryKey: [path] }),
  });
};

export const useUpdateCryptoCurrency = (marketplaceId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (settings: CryptoCurrencySettings) => {
      if (!marketplaceId) throw new Error('Select a marketplace first');
      return patchData(cryptoDashboardPath(marketplaceId, 'currencies'), settings, 'crm');
    },
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.cryptoDashboard, marketplaceId] }),
        ...(['currencies', 'static-wallets', 'audit'] satisfies CryptoSection[]).map((section) =>
          queryClient.invalidateQueries({ queryKey: [cryptoDashboardPath(marketplaceId, section)] })
        ),
      ]),
  });
};

export type CryptoWalletObservation = {
  status: 'matched' | 'missing' | 'conflict' | 'unavailable';
  checkedAt: string;
  balance: string | null;
  address: string | null;
  tag: string | null;
};
export type CryptoClientOperation = {
  id: string;
  action: 'enable' | 'disable';
  state: 'requested' | 'confirmed' | 'uncertain';
  actorId: string;
  reason: string;
  createdAt: string;
};
export type CryptoWalletDetail = CryptoStaticWallet & {
  providerObservation: CryptoWalletObservation | null;
  affectedWallets: Pick<CryptoStaticWallet, 'id' | 'currency' | 'address' | 'tag' | 'state'>[];
  walletFingerprint: string;
  availabilityObservable: false;
  payments: { id: string; paymentId: string; state: string; deliveryState: string | null; createdAt: string }[];
  paymentLimit: number;
  operations: CryptoClientOperation[];
};
export const useCryptoWalletDetail = (marketplaceId: string, walletId: string) =>
  useQuery({
    queryKey: [QUERY_KEYS.cryptoDashboard, marketplaceId, 'wallet', walletId],
    queryFn: () =>
      getData<CryptoWalletDetail>(
        cryptoDashboardPath(marketplaceId, `static-wallets/${encodeURIComponent(walletId)}`),
        undefined,
        'crm'
      ),
    enabled: !!marketplaceId && !!walletId,
  });
export const useCryptoWalletCommand = (marketplaceId: string, walletId: string) => {
  const cache = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: ({
      command,
      body,
    }: {
      command: 'refresh' | 'availability';
      body: {
        reason: string;
        confirmed?: boolean;
        requestId?: string;
        action?: 'enable' | 'disable';
        walletFingerprint?: string;
      };
    }) => {
      if (!marketplaceId || !walletId) throw new Error('Select a marketplace and wallet first');
      return postData<CryptoWalletDetail | CryptoClientOperation>(
        cryptoDashboardPath(marketplaceId, `static-wallets/${encodeURIComponent(walletId)}/${command}`),
        body,
        'crm'
      );
    },
    onSettled: () =>
      Promise.all([
        cache.invalidateQueries({ queryKey: [QUERY_KEYS.cryptoDashboard, marketplaceId] }),
        cache.invalidateQueries({ queryKey: [cryptoDashboardPath(marketplaceId, 'static-wallets')] }),
        cache.invalidateQueries({ queryKey: [cryptoDashboardPath(marketplaceId, 'audit')] }),
      ]),
  });
};
export const useCryptoHealth = (marketplaceId: string) =>
  useQuery({
    queryKey: [QUERY_KEYS.cryptoDashboard, marketplaceId, 'health'],
    queryFn: async () =>
      cryptoHealthSchema.parse(await getData<unknown>(cryptoDashboardPath(marketplaceId, 'health'), undefined, 'crm')),
    enabled: !!marketplaceId,
    retry: false,
  });
